import { useState } from 'react'
import {
  Stack, Text, Table, Button, Badge, Modal,
  TextInput, Textarea, Select, NumberInput, Group,
  Switch, Alert, Tooltip,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconPlus, IconEdit, IconAlertCircle, IconCategory } from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { useCurrentTenant } from '@/store/auth'
import { DataCard, EmptyState } from '@/components/ui'
import { formatApiError } from '@/api/errors'
import { mensagemDeAtendimento, type AtendimentoPrevisto } from '@/components/chamados/AtendidoPor'

interface Category {
  id: number
  tenant_id: number | null
  name: string
  description: string
  default_priority: string
  default_request_type: string
  sla_first_response_hours: number | null
  sla_resolution_hours: number | null
  sla_pause_on_waiting: boolean
  auto_close_after_days: number
  is_active: boolean
  requires_attachments: boolean
  pre_approved: boolean
  /** Quem atende o chamado desta categoria (TASK-12181); nulos = padrão da plataforma. */
  default_attending_org_id?: number | null
  default_queue_id?: number | null
  atendido_por?: string | null
}

const PRIORIDADE_OPTIONS = [
  { value: 'low', label: 'Baixa' },
  { value: 'medium', label: 'Média' },
  { value: 'high', label: 'Alta' },
  { value: 'critical', label: 'Crítica' },
]

const TIPO_OPTIONS = [
  { value: 'approval',        label: 'Aprovação' },
  { value: 'incident',        label: 'Incidente' },
  { value: 'service_request', label: 'Serviço' },
  { value: 'change_request',  label: 'Mudança' },
  { value: 'gmud',            label: 'GMUD' },
]

/** Valor que o modal nao conhece (enum novo no backend) aparece cru, em vez de sumir. */
function rotuloDe(options: { value: string; label: string }[], value: string): string {
  return options.find((o) => o.value === value)?.label ?? value
}

interface CategoryPayload {
  name: string
  description: string
  default_priority: string
  default_request_type: string
  sla_first_response_hours?: number
  sla_resolution_hours?: number
  sla_pause_on_waiting: boolean
  auto_close_after_days: number
  is_active: boolean
  requires_attachments: boolean
  pre_approved: boolean
  default_attending_org_id: number | null
  default_queue_id: number | null
}

function CategoryModal({
  opened, onClose, initial, tenantId,
}: {
  opened: boolean
  onClose: () => void
  initial: Category | null
  tenantId: number | undefined
}) {
  const queryClient = useQueryClient()
  const isEdit = !!initial

  const [name, setName] = useState(initial?.name ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [priority, setPriority] = useState(initial?.default_priority ?? 'medium')
  const [rtype, setRtype] = useState(initial?.default_request_type ?? 'service_request')
  const [slaFirst, setSlaFirst] = useState<number | string>(initial?.sla_first_response_hours ?? '')
  const [slaRes, setSlaRes] = useState<number | string>(initial?.sla_resolution_hours ?? '')
  const [pauseOnWaiting, setPauseOnWaiting] = useState(initial?.sla_pause_on_waiting ?? true)
  const [autoClose, setAutoClose] = useState<number | string>(initial?.auto_close_after_days ?? 3)
  const [isActive, setIsActive] = useState(initial?.is_active ?? true)
  const [requiresAttachments, setRequiresAttachments] = useState(initial?.requires_attachments ?? false)
  const [preApproved, setPreApproved] = useState(initial?.pre_approved ?? false)
  // "org:fila", ou nulo para cair no padrão da plataforma.
  const [destino, setDestino] = useState<string | null>(
    initial?.default_attending_org_id && initial?.default_queue_id
      ? `${initial.default_attending_org_id}:${initial.default_queue_id}` : null,
  )
  // As opções são as Organizações que podem atender ESTE tenant: a mesma regra que o backend
  // aplica ao gravar (fila interna só da Organização que contém o tenant).
  const { data: previsto } = useQuery<AtendimentoPrevisto>({
    queryKey: ['chamados-atendimento', tenantId, null],
    queryFn: async () => (await api.get(`/v1/tenants/${tenantId}/chamados/atendimento`)).data,
    enabled: opened && !!tenantId,
  })
  const opcoesDeDestino = (previsto?.opcoes ?? []).map((o) => ({
    group: o.org_nome,
    items: o.filas.map((f) => ({ value: `${o.org_id}:${f.fila_id}`, label: `${o.org_nome} · ${f.nome}` })),
  }))

  const mutation = useMutation({
    mutationFn: async (payload: CategoryPayload) => {
      if (isEdit && initial) {
        await api.put(`/v1/tenants/${tenantId}/ticket-categories/${initial.id}`, payload)
      } else {
        await api.post(`/v1/tenants/${tenantId}/ticket-categories`, payload)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-categories', tenantId] })
      notifications.show({ color: 'green', message: `Categoria ${isEdit ? 'atualizada' : 'criada'} com sucesso!` })
      onClose()
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      notifications.show({ color: 'red', message: mensagemDeAtendimento(detail) ?? formatApiError(err, 'Falha ao salvar a categoria.') })
    },
  })

  const handleSubmit = () => {
    if (!name.trim()) {
      notifications.show({ color: 'orange', message: 'Nome é obrigatório.' })
      return
    }
    mutation.mutate({
      name: name.trim(),
      description: description.trim(),
      default_priority: priority,
      default_request_type: rtype,
      sla_first_response_hours: slaFirst ? Number(slaFirst) : undefined,
      sla_resolution_hours: slaRes ? Number(slaRes) : undefined,
      sla_pause_on_waiting: pauseOnWaiting,
      auto_close_after_days: Number(autoClose) || 3,
      is_active: isActive,
      requires_attachments: requiresAttachments,
      pre_approved: preApproved,
      // Sempre os dois: nulo explícito limpa e devolve a categoria ao padrão da plataforma.
      default_attending_org_id: destino ? Number(destino.split(':')[0]) : null,
      default_queue_id: destino ? Number(destino.split(':')[1]) : null,
    })
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>{isEdit ? 'Editar Categoria' : 'Nova Categoria'}</Text>}
      size="lg"
    >
      <Stack gap="sm">
        <TextInput label="Nome" required value={name} onChange={(e) => setName(e.target.value)} />
        <Textarea label="Descrição" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        <Group grow>
          <Select
            label="Prioridade padrão"
            value={priority}
            onChange={(v) => setPriority(v ?? 'medium')}
            data={PRIORIDADE_OPTIONS}
          />
          <Select
            label="Tipo padrão"
            value={rtype}
            onChange={(v) => setRtype(v ?? 'service_request')}
            data={TIPO_OPTIONS}
          />
        </Group>
        <Group grow>
          <NumberInput
            label="SLA 1ª Resposta (horas)"
            placeholder="Ex: 4"
            min={0}
            value={slaFirst}
            onChange={setSlaFirst}
          />
          <NumberInput
            label="SLA Resolução (horas)"
            placeholder="Ex: 24"
            min={0}
            value={slaRes}
            onChange={setSlaRes}
          />
        </Group>
        <Group>
          <NumberInput
            label="Auto-fechamento após (dias)"
            min={1}
            value={autoClose}
            onChange={setAutoClose}
            style={{ flex: 1 }}
          />
          <Switch
            label="Pausar SLA em Aguard. Cliente"
            checked={pauseOnWaiting}
            onChange={(e) => setPauseOnWaiting(e.target.checked)}
            mt="xl"
          />
          {isEdit && (
            <Switch
              label="Categoria ativa"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              mt="xl"
            />
          )}
        </Group>
        <Select
          label="Atendido por"
          description="Organização e fila que recebem o chamado desta categoria. Vazio: o padrão da plataforma."
          placeholder="Padrão da plataforma"
          data={opcoesDeDestino}
          value={destino}
          onChange={setDestino}
          clearable
          searchable
        />
        <Switch
          label="Exige evidência ao abrir chamado"
          description="Se ativo, o usuário só consegue criar chamado nesta categoria após anexar ao menos um arquivo"
          checked={requiresAttachments}
          onChange={(e) => setRequiresAttachments(e.currentTarget.checked)}
        />
        <Switch
          label="Mudança-padrão (pré-aprovada)"
          description="Se ativo, mudanças desta categoria pulam o voto do CCM e são aprovadas automaticamente ao passar no gate de completude"
          checked={preApproved}
          onChange={(e) => setPreApproved(e.currentTarget.checked)}
        />
        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose} disabled={mutation.isPending}>Cancelar</Button>
          <Button onClick={handleSubmit} loading={mutation.isPending}>Salvar</Button>
        </Group>
      </Stack>
    </Modal>
  )
}

export function Categorias() {
  const tenantId = useCurrentTenant()?.id
  const [modalOpen, setModalOpen] = useState(false)
  const [editCategory, setEditCategory] = useState<Category | null>(null)

  const { data: categories = [], isLoading, error } = useQuery<Category[]>({
    queryKey: ['ticket-categories', tenantId],
    queryFn: async () => {
      const res = await api.get<Category[]>(`/v1/tenants/${tenantId}/ticket-categories`)
      return res.data
    },
    enabled: !!tenantId,
  })
  // Ordem natural: C1, C2 … C10 (a alfabetica pura punha C10 antes de C2).
  const ordenadas = [...categories].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { numeric: true }))

  const handleNew = () => { setEditCategory(null); setModalOpen(true) }
  const handleEdit = (cat: Category) => {
    if (cat.tenant_id === null) {
      notifications.show({ color: 'orange', message: 'Categorias globais não podem ser editadas por tenant.' })
      return
    }
    setEditCategory(cat)
    setModalOpen(true)
  }

  return (
    <Stack gap="md">
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          Não foi possível carregar as categorias. Tente novamente em instantes.
        </Alert>
      )}

      <DataCard flush
        title="Categorias de Chamado"
        accent="primary"
        badge={{ value: categories.length, color: 'blue' }}
        loading={isLoading}
        extra={
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={handleNew} disabled={!tenantId}>
            Nova Categoria
          </Button>
        }
      >
        {categories.length === 0 ? (
          <EmptyState
            bare
            size="md"
            accent="info"
            icon={IconCategory}
            title="Nenhuma categoria cadastrada"
            description="Categorias organizam os chamados por área de atuação (infraestrutura, suporte, financeiro, etc.). Crie a primeira para começar."
            primaryAction={{
              label: 'Nova Categoria',
              icon: <IconPlus size={14} />,
              onClick: handleNew,
            }}
          />
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Nome</Table.Th>
                <Table.Th>Tipo</Table.Th>
                <Table.Th>Prioridade</Table.Th>
                <Table.Th>SLA 1ª Resp.</Table.Th>
                <Table.Th>SLA Resolução</Table.Th>
                <Table.Th>Atendido por</Table.Th>
                <Table.Th>Escopo</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Ações</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {ordenadas.map((cat) => (
                <Table.Tr key={cat.id}>
                  <Table.Td fw={500}>{cat.name}</Table.Td>
                  <Table.Td><Badge size="xs" variant="outline">{rotuloDe(TIPO_OPTIONS, cat.default_request_type)}</Badge></Table.Td>
                  <Table.Td><Badge size="xs" color={cat.default_priority === 'critical' ? 'red' : cat.default_priority === 'high' ? 'orange' : 'blue'} variant="light">{rotuloDe(PRIORIDADE_OPTIONS, cat.default_priority)}</Badge></Table.Td>
                  <Table.Td><Text size="xs">{cat.sla_first_response_hours ? `${cat.sla_first_response_hours}h` : '—'}</Text></Table.Td>
                  <Table.Td><Text size="xs">{cat.sla_resolution_hours ? `${cat.sla_resolution_hours}h` : '—'}</Text></Table.Td>
                  <Table.Td>
                    <Text size="xs" c={cat.atendido_por ? undefined : 'dimmed'}>{cat.atendido_por ?? 'Padrão da plataforma'}</Text>
                  </Table.Td>
                  <Table.Td>
                    {cat.tenant_id === null ? (
                      <Tooltip label="Categorias globais não podem ser editadas por tenant.">
                        <Badge size="xs" variant="light" color="violet">Global</Badge>
                      </Tooltip>
                    ) : (
                      <Badge size="xs" variant="light" color="gray">Tenant</Badge>
                    )}
                  </Table.Td>
                  <Table.Td><Badge size="xs" color={cat.is_active ? 'green' : 'gray'} variant="light">{cat.is_active ? 'Ativa' : 'Inativa'}</Badge></Table.Td>
                  <Table.Td>
                    {/* Global nao se edita pelo tenant: sem botao morto, o motivo mora
                        no tooltip do selo "Global". */}
                    {cat.tenant_id === null ? (
                      <Text size="xs" c="dimmed">Somente leitura</Text>
                    ) : (
                      <Button
                        size="xs"
                        variant="subtle"
                        leftSection={<IconEdit size={12} />}
                        onClick={() => handleEdit(cat)}
                      >
                        Editar
                      </Button>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </DataCard>

      <CategoryModal
        key={editCategory?.id ?? 'new'}
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        initial={editCategory}
        tenantId={tenantId ? Number(tenantId) : undefined}
      />
    </Stack>
  )
}
